import {
  useAuthStore,
  useNavStore,
  useDistributionStore,
  useMaintenanceStore,
  usePausedTimepointStore,
  useReviewStore,
} from "@/stores";
import { router } from "@/router/router";
import { PARTICIPANT_TIMEPOINT_STATES } from "@/constants/participantTimepointStates";
import { DISTRIBUTION_STATES } from "@/constants/distributionStates";
import { accessLog } from "./accessLog";

const request = (method) => {
  return (url, body) => {
    const requestOptions = {
      method,
      headers: authHeader(url),
    };
    requestOptions.headers["Accept"] = "application/json";
    if (method != "GET" && body) {
      requestOptions.headers["Content-Type"] = "application/json";
      requestOptions.body = JSON.stringify(body);
    }
    // Remember which session issued this request, so a late response can't
    // act on a session that has since logged out or switched token.
    const { api_token: requestApiToken } = useAuthStore();
    return fetch(url, requestOptions).then((response) =>
      handleResponse(response, requestApiToken),
    );
  };
};

/////////////////////////////////////////
// Helpers
/////////////////////////////////////////

const authHeader = (url) => {
  // return auth header with api_token if exists and request is to the api url
  const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);
  if (isApiUrl) {
    const { isAuthenticated, api_token } = useAuthStore();
    if (isAuthenticated) {
      return { Authorization: `Bearer ${api_token}` };
    }
  }
  return {};
};

const parseResponseBody = (response, text) => {
  if (!text || text.trim() === "") {
    return null;
  }

  const trimmedText = text.trim();
  const contentType = response.headers.get("content-type") ?? "";
  const looksLikeJson =
    contentType.includes("application/json") ||
    trimmedText.startsWith("{") ||
    trimmedText.startsWith("[");

  if (looksLikeJson) {
    try {
      return JSON.parse(trimmedText);
    } catch (error) {
      console.warn("Unable to parse response body as JSON.", error);
    }
  }

  if (contentType.startsWith("text/plain")) {
    return { message: trimmedText };
  }

  return { raw: trimmedText };
};

const handleResponse = (response, requestApiToken) => {
  return response.text().then((text) => {
    const data = parseResponseBody(response, text);
    const navStore = useNavStore();
    const maintenanceStore = useMaintenanceStore();
    const pausedTimepointStore = usePausedTimepointStore();

    if (!response.ok) {
      const error = { response: response, data: data };
      // auto logout if 401 - Unauthorized response returned from api
      if (response.status === 401) {
        const authStore = useAuthStore();
        const distributionStore = useDistributionStore();
        const reviewStore = useReviewStore();
        authStore.reset();
        navStore.reset();
        distributionStore.reset();
        reviewStore.reset();
        localStorage.clear();
        // Clear access log UUIDs from sessionStorage on auto-logout
        accessLog.clearAllAccessLogUuids();
        router.push({ name: "loggedOut" });
        return;
      }

      if (response.status === 403) {
        if (error.data?.code === "review_session_unavailable") {
          // Review expiry must not interrupt the participant questionnaire.
        } else if (
          error.data?.code === PARTICIPANT_TIMEPOINT_STATES.ALREADY_COMPLETED
        ) {
          navStore.push({ name: "alreadyCompleted" });
        } else {
          navStore.push({ name: "forbidden" });
        }
      }

      if (response.status === 503) {
        maintenanceStore.activate();
      }

      // Before login, ParticipantEntryView renders its own 423 error screen.
      if (
        response.status === 423 &&
        error.data?.code === DISTRIBUTION_STATES.TIMEPOINT_PAUSED &&
        requestApiToken !== null &&
        requestApiToken === useAuthStore().api_token
      ) {
        pausedTimepointStore.activate();
      }

      if (
        response.status === 404 ||
        response.status === 303 ||
        response.status === 423 ||
        response.status === 503
      ) {
        // Let navigation or the global maintenance/paused screen handle these states.
      } else {
        // alert("An error occurred.");
      }
      console.log(error);
      return Promise.reject(error);
    }

    if (response.status === 204) return Promise.resolve(response);

    return Promise.resolve(data);
  });
};

export const fetchWrapper = {
  get: request("GET"),
  post: request("POST"),
  put: request("PUT"),
  delete: request("DELETE"),
};
